import { infiniteQueryOptions, useMutation, useQuery, useQueryClient, type MutateOptions } from '@tanstack/react-query';
import type { NoteResponse, ConversationResponse } from '@blazeteams/sdk';
import { client } from '@blazeteams/sdk/client';
import { useAuthStore } from '@/store/auth-store';
import { requireCurrentSession, sessionScope, sessionRevision, sessionTenantId } from '@/features/auth/session-scope';
import { getLocaleSnapshot } from '@/lib/i18n/runtime';
import { LocalizedError } from '@/lib/i18n/errors';
import { readConversationList } from './conversation-list-contract';
import { readNoteWrite, writeUUID } from './message-write-contract';
import { noteKey, billingKey, contactHistoryKey } from './customer-panel-keys';

const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const timestamp = (value: unknown): value is string => typeof value === 'string' && /^\d{4}-\d\d-\d\dT/.test(value) && Number.isFinite(Date.parse(value));
const failure = (key: string) => new LocalizedError('inbox', 'railPanels.' + key);
function owner() { const state = useAuthStore.getState(); return { scope: sessionScope(state), revision: sessionRevision(), tenant: sessionTenantId(state), language: getLocaleSnapshot().locale }; }
type Owner = ReturnType<typeof owner>;
function current(intent: Owner, signal?: AbortSignal) {
  requireCurrentSession(intent.revision);
  if (signal?.aborted) throw new DOMException('Cancelled', 'AbortError');
  if (intent.scope === 'anonymous' || !writeUUID(intent.tenant)) throw failure('readFailed');
}
function envelope(value: unknown, tenant: string): Record<string, unknown> {
  if (!record(value) || value.success !== true || value.tenant_id !== undefined && value.tenant_id !== tenant || !record(value.data) || value.data.tenant_id !== undefined && value.data.tenant_id !== tenant) throw failure('readFailed');
  return value.data;
}
export function readNotes(value: unknown, conversationId: string, tenant: string): NoteResponse[] {
  const data = envelope(value, tenant);
  if (!Array.isArray(data.notes) || data.total !== data.notes.length) throw failure('notesFailed');
  const ids = new Set<string>();
  return data.notes.map((raw: unknown) => {
    if (!record(raw) || typeof raw.content !== 'string') throw failure('notesFailed');
    let note;
    try { note = readNoteWrite({ success: true, data: raw }, { conversationId, content: raw.content }, tenant); }
    catch { throw failure('notesFailed'); }
    if (!note.id || ids.has(note.id.toLowerCase())) throw failure('notesFailed');
    ids.add(note.id.toLowerCase());
    return note;
  });
}
export interface CustomerHistoryPage { items: ConversationResponse[]; total: number; nextOffset: number }
export const CUSTOMER_HISTORY_PAGE_SIZE = 50;
export function readCustomerHistory(value: unknown, contactId: string, tenant: string, offset: number): CustomerHistoryPage {
  if (!record(value) || value.success !== true || value.tenant_id !== undefined && value.tenant_id !== tenant) throw failure('historyFailed');
  let checked;
  try { checked = readConversationList(value, tenant); } catch { throw failure('historyFailed'); }
  const { rows, total } = checked;
  if (total === null || rows.length > CUSTOMER_HISTORY_PAGE_SIZE || !rows.length && offset < total || rows.length && total < offset + rows.length) throw failure('historyFailed');
  const items = rows.map(row => {
    if (!writeUUID(row.id) || row.contact_id !== contactId) throw failure('historyFailed');
    const projection: Record<string, unknown> = { id: row.id, contact_id: contactId };
    for (const key of ['status', 'subject', 'last_message_preview', 'created_at', 'last_message_at', 'resolved_at'] as const) {
      const value = row[key];
      if (value != null && (typeof value !== 'string' || ['created_at', 'last_message_at', 'resolved_at'].includes(key) && !timestamp(value))) throw failure('historyFailed');
      if (value != null) projection[key] = value;
    }
    return projection as ConversationResponse;
  });
  // Offset is based on raw server rows, never the UI's current-ticket filter.
  return { items, total, nextOffset: offset + rows.length };
}

export interface BillingCustomer { provider_customer_id: string; identity_doc: string; name: string; status: string; pending_balance: string; plan: string }
export interface BillingInvoice { number: string; amount: string; currency: string; due_at: string; status: string }
export type ConversationBillingResult = { kind: 'ok'; customer: BillingCustomer; invoices: BillingInvoice[] | null } | { kind: 'no_customer' };
export function readBilling(value: unknown, tenant: string): ConversationBillingResult {
  const data = envelope(value, tenant);
  if (data.customer === null) return { kind: 'no_customer' };
  const raw = data.customer;
  if (!record(raw)) throw failure('billingFailed');
  const fields = ['provider_customer_id', 'identity_doc', 'name', 'status', 'pending_balance', 'plan'] as const;
  if (fields.some(key => typeof raw[key] !== 'string') || raw.phones != null && (!Array.isArray(raw.phones) || raw.phones.some(phone => typeof phone !== 'string'))) throw failure('billingFailed');
  const customer = Object.fromEntries(fields.map(key => [key, raw[key]])) as unknown as BillingCustomer;
  if (data.invoices === null) return { kind: 'ok', customer, invoices: null }; // Native connector failed to read invoices; not an empty list.
  if (!Array.isArray(data.invoices)) throw failure('billingFailed');
  const invoices = data.invoices.map((row: unknown): BillingInvoice => {
    const fields = ['number', 'amount', 'currency', 'due_at', 'status'] as const;
    if (!record(row) || fields.some(key => typeof row[key] !== 'string')) throw failure('billingFailed');
    return Object.fromEntries(fields.map(key => [key, row[key]])) as unknown as BillingInvoice;
  });
  return { kind: 'ok', customer, invoices };
}
async function read(intent: Owner, url: string, path: Record<string, string>, signal: AbortSignal, key: string, query?: Record<string, string | number>) {
  current(intent, signal);
  let result;
  try { result = await client.get<unknown>({ url, path, signal, query, headers: { 'Accept-Language': intent.language } }); }
  catch { current(intent, signal); throw failure(key); }
  current(intent, signal);
  return result;
}
export const customerPanelQueries = {
  notes: (id: string) => {
    const intent = owner();
    return { queryKey: noteKey(id, intent.scope), enabled: writeUUID(id) && intent.scope !== 'anonymous', retry: false,
      queryFn: async ({ signal }: { signal: AbortSignal }) => {
        const result = await read(intent, '/inbox/conversations/{id}/notes', { id }, signal, 'notesFailed');
        if (result.error || result.response?.status !== 200) throw failure(result.response?.status === 403 || result.response?.status === 404 ? 'unavailable' : 'notesFailed');
        return readNotes(result.data, id, intent.tenant);
      } };
  },
  history: (id?: string) => {
    const intent = owner();
    return infiniteQueryOptions({ queryKey: contactHistoryKey(id, intent.scope), enabled: writeUUID(id) && intent.scope !== 'anonymous', retry: false, initialPageParam: 0,
      queryFn: async ({ pageParam, signal }): Promise<CustomerHistoryPage> => {
        if (!writeUUID(id)) throw failure('historyFailed');
        const result = await read(intent, '/inbox/conversations', {}, signal, 'historyFailed', { contact_id: id, limit: CUSTOMER_HISTORY_PAGE_SIZE, offset: pageParam, sort: 'newest' });
        if (result.error || result.response?.status !== 200) throw failure('historyFailed');
        return readCustomerHistory(result.data, id, intent.tenant, pageParam);
      }, getNextPageParam: page => page.items.length && page.nextOffset < page.total ? page.nextOffset : undefined });
  },
  billing: (id: string) => {
    const intent = owner();
    return { queryKey: billingKey(id, intent.scope), enabled: writeUUID(id) && intent.scope !== 'anonymous', retry: false, staleTime: 60_000,
      queryFn: async ({ signal }: { signal: AbortSignal }): Promise<ConversationBillingResult> => {
        const result = await read(intent, '/inbox/conversations/{id}/billing', { id }, signal, 'billingFailed');
        if (result.error || result.response?.status !== 200) {
          const raw = result.error, status = result.response?.status;
          if (record(raw) && raw.success === false && record(raw.error)) {
            if (status === 404 && raw.error.code === 'no_isp_customer') return { kind: 'no_customer' };
            if (status === 404 && raw.error.code === 'not_found') throw failure('unavailable');
            if (status === 502 && raw.error.code === 'billing_unavailable') throw failure('billingUnavailable');
          }
          throw failure('billingFailed');
        }
        return readBilling(result.data, intent.tenant);
      } };
  },
};
export function historyRows(pages?: CustomerHistoryPage[]): ConversationResponse[] {
  const seen = new Set<string>();
  return (pages ?? []).flatMap(page => page.items).filter(row => !!row.id && !seen.has(row.id.toLowerCase()) && !!seen.add(row.id.toLowerCase()));
}

/** Both the rail and interleaved thread must hide prior notes after access loss. */
export function useConversationNotes(id: string) {
  const result = useQuery(customerPanelQueries.notes(id));
  const unavailable = result.error instanceof LocalizedError && result.error.key === 'railPanels.unavailable';
  return { ...result, data: unavailable ? undefined : result.data };
}

type NoteInput = { conversationId: string; noteId: string; content?: string };
type NoteIntent = Owner & { input: NoteInput };
export class NoteChangeError extends LocalizedError {
  constructor(readonly uncertain = true) { super('inbox', 'railPanels.' + (uncertain ? 'noteUnconfirmed' : 'noteInvalid')); }
}
function useNoteChange(method: 'PUT' | 'DELETE') {
  const qc = useQueryClient();
  const mutation = useMutation<NoteResponse | void, Error, NoteIntent>({ retry: false, gcTime: 0, scope: { id: JSON.stringify(['inbox-message-writes', sessionScope(useAuthStore.getState())]) },
    mutationFn: async intent => {
      current(intent);
      const { input } = intent;
      if (!writeUUID(input.conversationId) || !writeUUID(input.noteId) || method === 'PUT' && (typeof input.content !== 'string' || !input.content.trim() || new TextEncoder().encode(input.content).length > 5000)) throw new NoteChangeError(false);
      let result;
      try { result = await client.request<unknown>({ url: '/inbox/conversations/{id}/notes/{noteId}', method, path: { id: input.conversationId, noteId: input.noteId }, ...(method === 'PUT' ? { body: { content: input.content } } : {}), headers: { 'Accept-Language': intent.language } }); }
      catch { current(intent); throw new NoteChangeError(); }
      current(intent);
      if (result.error || result.response?.status !== 200) throw new NoteChangeError();
      if (method === 'DELETE') { if (!record(result.data) || result.data.success !== true || result.data.tenant_id !== undefined && result.data.tenant_id !== intent.tenant || result.data.conversation_id !== undefined && result.data.conversation_id !== input.conversationId || result.data.note_id !== undefined && result.data.note_id !== input.noteId) throw new NoteChangeError(); return; }
      let note;
      try { note = readNoteWrite(result.data, { conversationId: input.conversationId, content: input.content! }, intent.tenant); }
      catch { throw new NoteChangeError(); }
      if (note.id !== input.noteId) throw new NoteChangeError();
      return note;
    },
    onSettled: (_, __, intent) => { if (intent.revision === sessionRevision()) void qc.invalidateQueries({ queryKey: noteKey(intent.input.conversationId, intent.scope), exact: true }); },
  });
  const guard = (options?: MutateOptions<NoteResponse | void, Error, NoteIntent>): MutateOptions<NoteResponse | void, Error, NoteIntent> => ({
    onSuccess: (...args) => { if (args[1].revision === sessionRevision()) options?.onSuccess?.(...args); },
    onError: (...args) => { if (args[1].revision === sessionRevision()) options?.onError?.(...args); },
    onSettled: (...args) => { if (args[2].revision === sessionRevision()) options?.onSettled?.(...args); },
  });
  return { ...mutation, mutate: (input: NoteInput, options?: MutateOptions<NoteResponse | void, Error, NoteIntent>) => mutation.mutate({ ...owner(), input: { ...input } }, guard(options)) };
}
export const useUpdateNote = () => useNoteChange('PUT');
export const useDeleteNote = () => useNoteChange('DELETE');
